// src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, ReactNode, useContext, useCallback } from 'react';
import { Author } from '../api/Author';
import { clearAuthSession, resolveAuthSession } from './authSession';
import { subscribeToAuthenticatedAuthor } from './authRealtime';
import tokenService, { isPwaMode } from '../utils/tokenService';

interface AuthContextType {
  author: Author | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  checkAuthentication: () => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [author, setAuthor] = useState<Author | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const checkAuthentication = useCallback(async () => {
    setLoading(true);
    setError(null);

    const authSession = await resolveAuthSession();

    setAuthor(authSession.author);
    setToken(authSession.token);
    setError(authSession.error);
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    await clearAuthSession();
    setAuthor(null);
    setToken(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    const bootstrapAuthentication = async () => {
      await tokenService.handleTokenFromUrl({ cleanUrl: isPwaMode() });
      await checkAuthentication();
    };

    void bootstrapAuthentication();
  }, [checkAuthentication]);

  // Realtime listener for authenticated author updates
  useEffect(() => {
    if (author?.id) {
      return subscribeToAuthenticatedAuthor({
        authorId: author.id,
        setAuthor,
      });
    }
  }, [author?.id]);

  return (
    <AuthContext.Provider value={{ author, token, loading, error, checkAuthentication, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
